import React, { useState } from 'react';
import { XSIcon, XSIconInput } from '../component/icon/XSIcon';
import { XSButton } from '../component/button/XSButton';
import { unique } from '../util/unique';
import { without } from '../util/without';
import './useCheckable.css';

export type UseCheckableOutput<T> = {
    checkedValues: T[];
    renderElementFor(value: Value<T>, type?: 'icon' | 'td'): React.ReactNode;
    toggleValue(value: Value<T>): void;
    isChecked(value: Value<T>): boolean;
    reset: () => void;
    getState(value: Value<T>): {
        status: 'all' | 'none' | 'partial' | 'disabled';
        icon: string;
    };
};

type InputOptions<T> = {
    mapValue?(item: T): string | number;
    isValid?(item: T): boolean;
};

type Value<T> = T | T[];

export function useCheckable<T>(values: T[], options?: InputOptions<T>): UseCheckableOutput<T> {
    const [checkedValues, setCheckedValues] = useState<T[]>([]);

    function renderElementFor(value: Value<T>, type?: 'icon' | 'td') {
        const state = getState(value);

        if (type === 'icon') {
            let color: XSIconInput['color'];

            if (state.status === 'all' || state.status === 'partial') {
                color = 'primary';
            } else if (state.status === 'disabled') {
                color = 'ghost';
            }

            return <XSIcon color={color}>{state.icon}</XSIcon>;
        }

        return (
            <XSButton
                onClick={() => toggleValue(value)}
                icon={state.icon}
                color={state.status === 'all' || state.status === 'partial' ? 'primary' : 'default'}
                fill={'none'}
                disabled={state.status === 'disabled'}
            />
        );
    }

    function getState(value: Value<T>) {
        const values = toArray(value).filter(isCheckable);
        const checked = values.filter((value) => checkedValues.includes(value));

        let status: 'all' | 'none' | 'partial' | 'disabled';
        let icon: string;

        if (values.length === 0) {
            status = 'disabled';
            icon = 'disabled_by_default';
        } else if (values.length === checked.length) {
            status = 'all';
            icon = 'check_box';
        } else if (checked.length > 0) {
            status = 'partial';
            icon = 'indeterminate_check_box';
        } else {
            status = 'none';
            icon = 'check_box_outline_blank';
        }

        return { status, icon };
    }

    function isCheckable(value: Value<T>) {
        if (options?.isValid) {
            return Boolean(toArray(value).find((item) => options?.isValid?.(item)));
        }

        return true;
    }

    function toggleValue(value: Value<T>) {
        const values = toArray(value);
        const state = getState(value);

        if (state.status === 'all') {
            setValues(without(checkedValues, values));
        } else {
            setValues([...checkedValues, ...values]);
        }
    }

    function toArray(value: Value<T>) {
        return Array.isArray(value) ? value : [value];
    }

    function setValues(value: Value<T>) {
        const uniqueValues = unique(toArray(value));
        setCheckedValues(uniqueValues);
    }

    return {
        checkedValues: values.filter((value) => checkedValues.includes(value)),
        renderElementFor: renderElementFor,
        toggleValue: toggleValue,
        isChecked: (value: Value<T>) => getState(value).status === 'all',
        reset: () => setValues([]),
        getState: getState,
    };
}
